import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./NewRegistration.css";

import logo from "../../assets/expert_logo.webp"; // change if your logo path differs
import { loginWithApi } from "../../services/auth";
import { checkMobileExists, registerKendraApi, sendOtpApi } from "../../services/registration";

const STATES_MR = [
    "अंदमान आणि निकोबार बेट", "आंध्र प्रदेश", "अरुणाचल प्रदेश", "असम", "बिहार",
    "चंदीगड", "छत्तीसगड", "दादरा आणि नगर हवेली", "दमन आणि दीव", "दिल्ली",
    "गोवा", "गुजरात", "हरियाणा", "हिमाचल प्रदेश", "जम्मू आणि काश्मीर",
    "झारखंड", "कर्नाटक", "केरळ", "लक्षद्वीप", "मध्य प्रदेश", "महाराष्ट्र",
    "मणिपूर", "मेघालय", "मिझोरम", "नगालँड", "ओडिशा", "पुदुच्चेरी",
    "पंजाब", "राजस्थान", "सिक्कीम", "तामिळनाडू", "तेलंगणा",
    "त्रिपुरा", "उत्तर प्रदेश", "उत्तराखंड", "पश्चिम बंगाल", "लडाख"
];

// You can later localize these; keeping simple English labels for web
const SANK_TYPES = [
    "Fat Only",
    "CLR/Degree Only",
    "Fat + SNF",
    "Fat + CLR/Degree",
    "Fat + CLR/Degree + Auto SNF",
    "Liter Only",
];

const MILK_TYPES = ["Cow", "Buffalo", "Cow + Buffalo"];
const SHIFTS = ["Morning", "Evening", "Morning + Evening", "Auto (Morning + Evening)"];
const PAY_CYCLES = [
    "01-10, 11-20, 21-ME",
    "01-15, 16-ME",
    "01-ME",
    "01-05, 06-10, 11-15, 16-20, 21-25, 26-ME",
    "06-15, 16-25, 26-05",
    "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
];

function randOtp4() {
    return String(Math.floor(Math.random() * 10000)).padStart(4, "0");
}

export default function NewRegistration() {
    const nav = useNavigate();

    const [step, setStep] = useState(1); // 1..3 (web screenshot)
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    // step 1
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [mobile, setMobile] = useState("");
    const [terms, setTerms] = useState(true);

    const [hasReferral, setHasReferral] = useState(false);
    const [referral, setReferral] = useState("");

    const [otpRequested, setOtpRequested] = useState(false);
    const [otp, setOtp] = useState("");
    const [newPass, setNewPass] = useState("");
    const [sentOtp, setSentOtp] = useState(null);

    const [mobileExists, setMobileExistsState] = useState(false);

    // step 2
    const [dairyName, setDairyName] = useState("");
    const [village, setVillage] = useState("");
    const [taluka, setTaluka] = useState("");
    const [district, setDistrict] = useState("");
    const [state, setState] = useState("");

    // step 3
    const [sankType, setSankType] = useState("");
    const [milkType, setMilkType] = useState("");
    const [shift, setShift] = useState("");
    const [paymentCycle, setPaymentCycle] = useState("");

    // debounce mobile check (like Android softCheckMobile)
    const mobileTimer = useRef(null);
    useEffect(() => {
        setMsg("");
        const digits = mobile.replace(/\D+/g, "");

        if (mobileTimer.current) clearTimeout(mobileTimer.current);

        if (digits.length < 10) {
            setMobileExistsState(false);
            return;
        }

        mobileTimer.current = setTimeout(async () => {
            try {
                const exists = await checkMobileExists(digits);
                setMobileExistsState(exists);

                if (exists) {
                    // same behavior: clear & block
                    setMsg("Mobile number already exists.");
                    setMobile("");
                }
            } catch {
                // silent like Android
            }
        }, 300);

        return () => {
            if (mobileTimer.current) clearTimeout(mobileTimer.current);
        };
    }, [mobile]);

    const canRequestOtp = useMemo(() => {
        const digits = mobile.replace(/\D+/g, "");
        return (
            firstName.trim() &&
            lastName.trim() &&
            digits.length === 10 &&
            !mobileExists &&
            terms
        );
    }, [firstName, lastName, mobile, mobileExists, terms]);

    const canGoStep2 = useMemo(() => {
        const digits = mobile.replace(/\D+/g, "");
        if (!(firstName.trim() && lastName.trim() && digits.length === 10)) return false;
        if (!otpRequested) return false;
        if (!otp.trim()) return false;
        if (!newPass.trim()) return false;
        if (!terms) return false;
        if (!sentOtp) return false;
        return otp.trim() === sentOtp;
    }, [firstName, lastName, mobile, otpRequested, otp, newPass, terms, sentOtp]);

    const canGoStep3 = useMemo(() => {
        return (
            dairyName.trim() &&
            village.trim() &&
            taluka.trim() &&
            district.trim() &&
            state.trim()
        );
    }, [dairyName, village, taluka, district, state]);

    const canFinish = useMemo(() => {
        return sankType && milkType && shift && paymentCycle;
    }, [sankType, milkType, shift, paymentCycle]);

    async function requestOtp() {
        if (!canRequestOtp) {
            setMsg("Please fill required fields and accept Terms & Conditions.");
            return;
        }

        const digits = mobile.replace(/\D+/g, "");
        const generated = randOtp4();

        setLoading(true);
        setMsg("");

        const res = await sendOtpApi(digits, generated);

        if (res.ok) {
            setSentOtp(generated);
            setOtpRequested(true);
            setMsg("OTP sent successfully.");
        } else {
            console.log("OTP failed:", res.status, res.data);
            setMsg("Failed to send OTP. Please try again.");
        }

        setLoading(false);

    }

    function next() {
        setMsg("");

        if (step === 1) {
            if (!canGoStep2) {
                setMsg("Please verify OTP and set password.");
                return;
            }
            setStep(2);
            return;
        }

        if (step === 2) {
            if (!canGoStep3) {
                setMsg("Please fill all Center Details.");
                return;
            }
            setStep(3);
            return;
        }
    }

    function back() {
        setMsg("");
        if (step === 1) {
            nav("/");
            return;
        }
        setStep((s) => Math.max(1, s - 1));
    }

    async function finish() {
        if (!canFinish) {
            setMsg("Please select all Center Settings.");
            return;
        }

        const digits = mobile.replace(/\D+/g, "");

        setLoading(true);
        setMsg("");

        try {
            const res = await registerKendraApi({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                mobile: digits,
                newPass: newPass,
                enname: dairyName.trim(),
                city: village.trim(),
                tal: taluka.trim(),
                dist: district.trim(),
                state: state.trim(),
            });

            if (!res?.ok) {
                setMsg(res?.error || "Registration failed. Please try again.");
                setLoading(false);
                return;
            }

            // Auto-login like Android doPostSaveAutoLogin()
            await loginWithApi(digits, newPass);
            nav("/dashboard", { replace: true });
        } catch {
            setMsg("Network / server error. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="nrPage">
            <div className="nrTop">
                <div className="nrBrand" onClick={() => nav("/")}>
                    <img className="nrLogo" src={logo} alt="Expert Dairy" />
                </div>

                <div className="nrLang">
                    <span>English</span>
                    <span className="nrCaret">▾</span>
                </div>
            </div>

            <h1 className="nrTitle">Create an Account</h1>

            <div className="nrLayout">
                {/* LEFT PANEL */}
                <div className="nrCharges">
                    <h3>Application Charges:</h3>

                    <div className="nrPlan">
                        <div className="nrPlanHead">
                            <div className="nrPlanName">7 Days Trial</div>
                            <div className="nrPlanPrice">0.00 ₹</div>
                        </div>
                        <ul>
                            <li>7 days trial with standard plan all features</li>
                        </ul>
                    </div>

                    <div className="nrPlan">
                        <div className="nrPlanHead">
                            <div className="nrPlanName">Standard Plan - One Year - 2500</div>
                            <div className="nrPlanPrice">2,500.00 ₹</div>
                        </div>
                        <ul>
                            <li>One year subscription with basic features</li>
                        </ul>
                    </div>

                    <div className="nrPlan">
                        <div className="nrPlanHead">
                            <div className="nrPlanName">Advance Plan - One Year - 3200</div>
                            <div className="nrPlanPrice">3,200.00 ₹</div>
                        </div>
                        <ul>
                            <li>Standard Plan all features</li>
                            <li>Multiple Ratechart</li>
                            <li>Milk Rejection</li>
                            <li>200 Active Farmers</li>
                            <li>Hardware Automation</li>
                            <li>Multiple collection</li>
                            <li>Ratechart Download</li>
                            <li>Can Management</li>
                            <li>Unlimited InActive Farmers</li>
                        </ul>
                    </div>
                </div>

                {/* RIGHT PANEL */}
                <div className="nrCard">
                    <div className="nrStepper">
                        <div className={`nrStep ${step === 1 ? "active" : ""}`}>
                            <div className="nrDot">1</div>
                            <div className="nrStepLabel">Personal Details</div>
                        </div>
                        <div className={`nrStep ${step === 2 ? "active" : ""}`}>
                            <div className="nrDot">2</div>
                            <div className="nrStepLabel">Center Details</div>
                        </div>
                        <div className={`nrStep ${step === 3 ? "active" : ""}`}>
                            <div className="nrDot">3</div>
                            <div className="nrStepLabel">Center Settings</div>
                        </div>
                    </div>

                    {msg ? <div className="nrMsg">{msg}</div> : null}

                    {/* STEP 1 */}
                    {step === 1 && (
                        <div className="nrForm">
                            <label>First Name</label>
                            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />

                            <label>Last Name</label>
                            <input value={lastName} onChange={(e) => setLastName(e.target.value)} />

                            <label>Mobile</label>
                            <input
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value)}
                                inputMode="numeric"
                                maxLength={10}
                            />

                            <div className="nrRow">
                                <input
                                    type="checkbox"
                                    checked={terms}
                                    onChange={(e) => setTerms(e.target.checked)}
                                />
                                <div>
                                    I have read and agreed to the{" "}
                                    <span className="nrLink">Terms &amp; Conditions</span>
                                </div>
                            </div>

                            <div className="nrRow">
                                <input
                                    type="checkbox"
                                    checked={hasReferral}
                                    onChange={(e) => setHasReferral(e.target.checked)}
                                />
                                <div>I have a referral code / Dealer Code / Promo Code</div>
                            </div>

                            {hasReferral ? (
                                <input
                                    className="nrReferral"
                                    placeholder="Enter referral / promo code"
                                    value={referral}
                                    onChange={(e) => setReferral(e.target.value)}
                                />
                            ) : null}

                            <div className="nrActions">
                                <button className="nrBackLink" type="button" onClick={() => nav("/")}>
                                    ← Back to Login
                                </button>

                                <button
                                    className="nrPrimary"
                                    type="button"
                                    onClick={requestOtp}
                                    disabled={loading || !canRequestOtp}
                                >
                                    {loading ? "Sending..." : "Request OTP"}
                                </button>
                            </div>

                            {otpRequested ? (
                                <>
                                    <label style={{ marginTop: 12 }}>OTP</label>
                                    <input value={otp} onChange={(e) => setOtp(e.target.value)} inputMode="numeric" />

                                    <label>New Password</label>
                                    <input
                                        type="password"
                                        value={newPass}
                                        onChange={(e) => setNewPass(e.target.value)}
                                    />

                                    <div className="nrActions" style={{ marginTop: 14 }}>
                                        <button className="nrSecondary" type="button" onClick={back}>
                                            Back
                                        </button>

                                        <button
                                            className="nrPrimary"
                                            type="button"
                                            onClick={next}
                                            disabled={loading || !canGoStep2}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    )}

                    {/* STEP 2 */}
                    {step === 2 && (
                        <div className="nrForm">
                            <label>Dairy Name</label>
                            <input value={dairyName} onChange={(e) => setDairyName(e.target.value)} />

                            <label>Village</label>
                            <input value={village} onChange={(e) => setVillage(e.target.value)} />

                            <label>Taluka</label>
                            <input value={taluka} onChange={(e) => setTaluka(e.target.value)} />

                            <label>District</label>
                            <input value={district} onChange={(e) => setDistrict(e.target.value)} />

                            <label>State</label>
                            <select value={state} onChange={(e) => setState(e.target.value)}>
                                <option value="">Select State</option>
                                {STATES_MR.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>

                            <div className="nrActions">
                                <button className="nrSecondary" type="button" onClick={back}>
                                    Back
                                </button>
                                <button className="nrPrimary" type="button" onClick={next} disabled={!canGoStep3}>
                                    Next
                                </button>
                            </div>
                        </div>
                    )}

                    {/* STEP 3 */}
                    {step === 3 && (
                        <div className="nrForm">
                            <label>Sanklan Type</label>
                            <select value={sankType} onChange={(e) => setSankType(e.target.value)}>
                                <option value="">Select</option>
                                {SANK_TYPES.map((x) => (
                                    <option key={x} value={x}>
                                        {x}
                                    </option>
                                ))}
                            </select>

                            <label>Milk Type</label>
                            <select value={milkType} onChange={(e) => setMilkType(e.target.value)}>
                                <option value="">Select</option>
                                {MILK_TYPES.map((x) => (
                                    <option key={x} value={x}>
                                        {x}
                                    </option>
                                ))}
                            </select>

                            <label>Shift</label>
                            <select value={shift} onChange={(e) => setShift(e.target.value)}>
                                <option value="">Select</option>
                                {SHIFTS.map((x) => (
                                    <option key={x} value={x}>
                                        {x}
                                    </option>
                                ))}
                            </select>

                            <label>Payment Cycle</label>
                            <select value={paymentCycle} onChange={(e) => setPaymentCycle(e.target.value)}>
                                <option value="">Select</option>
                                {PAY_CYCLES.map((x) => (
                                    <option key={x} value={x}>
                                        {x}
                                    </option>
                                ))}
                            </select>

                            <div className="nrActions">
                                <button className="nrSecondary" type="button" onClick={back}>
                                    Back
                                </button>

                                <button className="nrPrimary" type="button" onClick={finish} disabled={loading || !canFinish}>
                                    {loading ? "Saving..." : "Finish"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
